import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import { differsAtCentPrecision } from '../validation-field-key.util';
import {
  buildRole15ColumnKey,
  buildRole19CandidateKeys,
  getRole15CrossSheetTargets,
  isRole15Empty,
  pickRole15Schema,
  Role15Schema,
} from './role15-statement-of-comprehensive-income.rules';
import { recomputeRole12WithRole13Inheritance } from './role12-statement-of-profit-or-loss.rules';
import {
  readSlice,
  resolveDimensionVisibility,
  resolvePrerequisiteValue,
  VisibilityCell,
} from './dimension-visibility.util';
import { getDimensionVisibilityConfig } from './dimension-visibility-registry';
import {
  accountingStandardToXbrlPrefix,
  applyLinkedInheritance,
  buildEqualitySnapshot,
  calculator,
  hasCalculatedFieldMismatch,
  isResolvedAccountingStandard,
  type LinkableControl,
} from './calc-diff.helper';
import { ROLE12_PROFIT_OR_LOSS_LINK_ROLE } from './link-roles';
import {
  pickRole19Schema,
  resolveRole19ColumnVisibility,
} from './role19-statement-of-changes-in-equity.rules';
import { SHEET_KEYS } from '../../../models/sheet-keys';

/**
 * Pure validator for Role 15 (Statement of Comprehensive Income).
 *
 * Mirrors the editor's role15-410000-statement-of-comprehensive-income.component.ts
 * `evaluateSheetStatus()` surface — i.e. which rows resolve to the 'Failed'
 * mapping status. The component also computes per-row 'Validated*' /
 * 'Edited' / 'Validated' states from API-mismatch baselines, equality
 * warnings, and decimal-format checks, but those map to
 * `hasSuggestedChanges` not `hasErrors` — and only `hasErrors` contributes
 * to the parity-check pair persisted on `SheetLevelStatusData`. We
 * intentionally restrict the pure path to the deterministic Failed drivers
 * so the parity-check harness can compare like-for-like.
 *
 * Failed drivers actually mirrored here:
 *
 *  1. **Cross-sheet ComprehensiveIncome / OtherComprehensiveIncome equality
 *     against Role 19** (Statement of Changes in Equity). The component
 *     compares Role 15's ComprehensiveIncome and OtherComprehensiveIncome
 *     totals against the matching equity-axis cells in Role 19's payload
 *     across the same four columns. A mismatch at cent precision
 *     produces a Failed row. (`checkCrossSheetEqualityWithRole19()`)
 *
 *  2. **Same-sheet ComprehensiveIncome ↔ ComprehensiveIncome_2 equality.**
 *     Both are calculated totals; a cent-level mismatch flags both cells
 *     Failed. Guarded on the headless path where the FE-only `_2` twin is
 *     absent. (`checkComprehensiveIncomePairCrossValidation()`)
 *
 *  3. **Conditional required fields** (`requiredIf` in the schema, via
 *     `checkConditionalRequiredErrors()`). The two attribution rows —
 *     `ComprehensiveIncomeAttributableToOwnersOfParent` and
 *     `...NoncontrollingInterests` — become required across EVERY VISIBLE
 *     Consolidated/Separate × Current/Previous column when
 *     `filingPrerequisites.NatureOfFinancialStatements` equals "Consolidated".
 *     Mirrors the editor's `validateConsolidatedRequiredFields()`.
 *
 * _History (both halves are P.O. rulings, and both covered MFRS and MPERS
 * together):_ driver 3 was REMOVED on 2026-06-03 — the `requiredIf` blocks were
 * stripped from both schemas, and this helper plus the editor's
 * `validateConsolidatedRequiredFields()` were deleted rather than left dormant.
 * It was REINSTATED on 2026-08-21 after the P.O. re-checked the source sheets.
 * See the *Implementation Notes* history line in the 15a/15b spec sheets.
 *
 * Drivers intentionally NOT mirrored (would not change `hasErrors`):
 *
 *  - API-mismatch warnings ("does not match the extracted sum") → these are
 *    Suggested Change / Validated*, not Failed.
 *  - Decimal-format errors (`invalidDecimal`) → set imperatively via
 *    setErrors() in the editor, but only on dirty/touched controls. The
 *    orchestrator runs over persisted payloads where dirty/touched are not
 *    available, so these would always evaluate to false in pure form
 *    anyway. The check is also tactically defensive (NaN guard on a number
 *    input that's already typed) and not load-bearing for Failed status.
 *  - Equality warnings via runEqualityValidations() → likewise stored as
 *    Suggested Change category in the component (do not include the literal
 *    "Statement of Changes in Equity" prefix that drives the cross-sheet
 *    Failed branch).
 *
 * ---- Cross-sheet dependency notes ----------------------------------------
 *
 *  - `filingPrerequisites` (Role 0) — required for Nature/Preparation gating
 *    of column visibility AND for the conditional-required rule. Tolerated
 *    absent: we now fall back to `filingInfo` for the two DEI-namespace gating
 *    fields (mirror of the role 12 first-listing-visit fix), so the listing
 *    path resolves visibility deterministically. If neither slice carries the
 *    key, visibility defaults to "all columns visible" and the `requiredIf`
 *    rule SKIPS — a rule whose driver has not loaded must not manufacture a
 *    blocking error (matches the component's pre-subscription posture).
 *  - `filingInfo` (Role 1) — read as a fallback for the two DEI gating
 *    fields when filingPrerequisites is absent. Both fields are persisted
 *    on filingInfo as well per role-1.form.json (lines 113 / 279).
 *  - `statementOfChangesInEquity` (Role 19) — required for the
 *    ComprehensiveIncome / OtherComprehensiveIncome equality check. When
 *    the bundle has no Role 19 slice, the check is skipped entirely (no
 *    reference data to compare). When the slice is present but the
 *    referenced row is missing under every candidate key (first-listing-
 *    visit hazard: SOCIE has not been opened yet so its payload predates
 *    the equity-axis row), we ALSO skip — coercing the absent row to 0
 *    would manufacture a false mismatch against role 15's calculated total.
 *
 * Note: per SheetDependencyService, Role 15 is also Role 19's *upstream*
 * (Role 19 reads from Role 15). The cycle is intentional — both sheets
 * publish totals that have to agree column-by-column.
 */

function readCrossSheetSlice(
  context: SheetValidationContext,
  sheet: string
): Record<string, any> | undefined {
  const bundle = context.crossSheetData;
  if (!bundle) return undefined;
  const slice = bundle.get(sheet as Parameters<typeof bundle.get>[0]);
  return slice as Record<string, any> | undefined;
}

/**
 * Human label for a visibility cell, used in error messages. The shared
 * `VisibilityCell` (from dimension-visibility.util) intentionally drops the
 * per-cell `label` the former role-local `Role15ColumnVisibility` carried, so
 * we derive the identical string here — `"<Dimension> <Current|Previous>"`,
 * reproducing the exact labels role 15 previously emitted ("Consolidated
 * Current", "Consolidated Previous", "Separate Current", "Separate Previous").
 */
function columnLabel(cell: VisibilityCell): string {
  const periodWord = cell.period === 'CurrentYear' ? 'Current' : 'Previous';
  return `${cell.dimension} ${periodWord}`;
}

/**
 * Shared, config-driven column-visibility for Role 15. Replaces the former
 * role-local `resolveRole15ColumnVisibility()` — the SAME resolver + central
 * registry that the mounted editor calls via
 * `CrossReferenceService.getVisibility()`, so the on-screen columns and the
 * validated columns cannot drift. Reads the two DEI gating fields
 * (Nature / preparation-type) prerequisites-first, then filingInfo (the
 * first-listing-visit fallback — the editor has not mounted on the listing
 * path, so filingPrerequisites is undefined, but both fields are also
 * persisted on the backend's filingInfo slice).
 */
function resolveVisibleColumns(
  context: SheetValidationContext
): VisibilityCell[] {
  return resolveDimensionVisibility(
    getDimensionVisibilityConfig(SHEET_KEYS.statementOfComprehensiveIncome)!,
    {
      filingPrerequisites: readSlice(context.crossSheetData, 'filingPrerequisites'),
      filingInfo: readSlice(context.crossSheetData, 'filingInfo'),
    }
  );
}

/**
 * Recompute Role 19's (SOCIE) calculated equity totals up-front and overlay
 * them on its persisted slice, so this sheet's cross-sheet equality compares
 * the SAME operand the mounted editor does.
 *
 * The editor reconciles SOCIE before comparing — `validateComprehensiveIncomeAgainstRole19()`
 * reads `lastRecomputedSheetBag` built by `buildSocieColumnContext` +
 * `recomputeReferencedSheets`. The headless path previously read Role 19's RAW
 * N8N-extracted grand total (`readCrossSheetSlice`, no recompute); when that
 * extraction was internally inconsistent (common for N8N output) it manufactured
 * a false blocking mismatch on the listing that vanished the moment the sheet was
 * opened (the editor's recompute reconciled it). This mirrors the SAME
 * recompute-up-front fix already applied to roles 8/12/19 (`buildEqualitySnapshot`
 * + `calculateTotals_610000`); role 15 was allow-listed for the pure verdict but
 * missed by that sweep.
 *
 * The recompute call is identical to role 19's own validator (`pickRole19Schema`
 * for `equityColumns` + `resolveRole19ColumnVisibility`), so the two never drift.
 * Returns `{}` when no Role 19 slice is present — the caller then treats the
 * referenced rows as absent (mirrors the prior `?? {}` posture), preserving the
 * "present-on-15, absent-on-19 → flag" behaviour the editor also exhibits.
 */
function recomputeRole19Snapshot(
  context: SheetValidationContext
): Record<string, any> {
  // GLOBAL RULE (PO, 2026-06-19, REVISED): the referenced Role-19 operand is the
  // recompute of its LATEST (live) slice — NEVER the N8N snapshot. The snapshot
  // is only ever compared against the recompute/latest of the SAME field (the
  // non-blocking "does not match the extracted sum" Suggested Change owned by
  // Role-19 itself); using it as a cross-sheet equality operand let an
  // internally-inconsistent extraction drive a false block. Recompute from the
  // live slice below — exactly what the mounted editor reads. (`crossSheetBaseline`
  // is intentionally no longer consulted here.)
  const role19Data = readCrossSheetSlice(
    context,
    SHEET_KEYS.statementOfChangesInEquity
  );
  if (!role19Data) return {};

  const schema = pickRole19Schema(context.accountingStandard);
  const equityColumns = schema.equityColumns ?? [];
  const visibleColumns = resolveRole19ColumnVisibility({
    filingPrerequisites: readSlice(context.crossSheetData, 'filingPrerequisites'),
    filingInfo: readSlice(context.crossSheetData, 'filingInfo'),
  });

  const recomputed = calculator.calculateTotals_610000(
    role19Data,
    accountingStandardToXbrlPrefix(context.accountingStandard),
    {
      hasEquityColumns: equityColumns.length > 0,
      equityColumns,
      visibleColumns,
    }
  );
  return buildEqualitySnapshot(role19Data, recomputed);
}

/**
 * Mirrors validateComprehensiveIncomeAgainstRole19(): compare Role 15's
 * ComprehensiveIncome and OtherComprehensiveIncome totals against the
 * matching equity-axis cells in Role 19's payload, across the same four
 * columns gated by visibility. Mismatch above tolerance → Failed row.
 *
 * `role15Snapshot` is the RECOMPUTED Role 15 overlay (see validate()), and Role
 * 19 is likewise recomputed via {@link recomputeRole19Snapshot} — so BOTH sides
 * are the reconciled totals the mounted editor compares, not the raw extraction.
 */
function checkCrossSheetEqualityWithRole19(
  role15Snapshot: Record<string, any>,
  context: SheetValidationContext
): Record<string, string> {
  const errors: Record<string, string> = {};

  // Column visibility comes from the SHARED resolver (filingPrerequisites-first,
  // then filingInfo) — see resolveVisibleColumns(). The filingInfo fallback
  // covers the listing-path first-visit hazard that would otherwise over-show
  // PreviousYear columns and manufacture mismatches against role 19 rows that
  // should not be compared.
  const role19Data = recomputeRole19Snapshot(context);
  const visibleColumns = resolveVisibleColumns(context);

  const targets = getRole15CrossSheetTargets(context.accountingStandard);
  const qnamesToCompare = [
    targets.comprehensiveIncomeQname,
    targets.otherComprehensiveIncomeQname,
  ];

  for (const qname of qnamesToCompare) {
    for (const column of visibleColumns) {
      if (!column.visible) continue;

      const role15Key = buildRole15ColumnKey(qname, column.dimension, column.period);
      const role15Value = role15Snapshot[role15Key];

      const candidates = buildRole19CandidateKeys(
        qname,
        column.dimension,
        column.period,
        context.accountingStandard
      );

      // Probe candidate keys for the first match; absent rows resolve to
      // undefined and coerce to 0 below — mirrors the editor's
      // validateComprehensiveIncomeAgainstRole19() which proceeds with
      // role19Num=0 when no candidate key is found. Parity with the editor
      // requires flagging the mismatch in that case (the editor already
      // does, and the parity-check harness was throwing on the divergence).
      let role19Value: unknown = undefined;
      let foundKey = '';
      for (const candidate of candidates) {
        if (candidate in role19Data) {
          role19Value = role19Data[candidate];
          foundKey = candidate;
          break;
        }
      }

      const role15Empty = isRole15Empty(role15Value);
      const role19Empty = !foundKey || isRole15Empty(role19Value);

      // Skip when both sides are empty — no data to compare.
      if (role15Empty && role19Empty) continue;

      // Mirror the component's coercion: empty side is treated as 0 (these
      // are calculated totals, so a missing total is meaningfully zero).
      const role15Num = role15Empty ? 0 : parseFloat(String(role15Value));
      const role19Num = role19Empty ? 0 : parseFloat(String(role19Value));

      // parseFloat('foo') = NaN; comparisons against NaN are always false,
      // so an unparseable side would silently skip. Treat NaN as zero to
      // match the editor's tolerant arithmetic.
      const safeRole15 = Number.isFinite(role15Num) ? role15Num : 0;
      const safeRole19 = Number.isFinite(role19Num) ? role19Num : 0;

      if (differsAtCentPrecision(safeRole15, safeRole19)) {
        errors[role15Key] =
          `${columnLabel(column)}: Mismatch in value provided for the same field. ` +
          `The value should match ${qname} in Statement of Changes in Equity.`;
      }
    }
  }

  return errors;
}

/**
 * Mirrors the editor's `validateConsolidatedRequiredFields()`: for every schema
 * control carrying a `requiredIf` block whose referenced cross-sheet field
 * equals the configured value, EVERY VISIBLE column must carry a value.
 *
 * Three details are load-bearing and were each chosen deliberately:
 *
 *  - **All visible columns, not just the Consolidated ones** (P.O., 2026-08-21).
 *    When Nature is "Consolidated" all four columns are on screen, and both
 *    attribution rows must be reported in each of them. A first-time filing
 *    hides the Previous-year pair, and those hidden cells are not required —
 *    visibility is resolved by the SAME shared resolver the editor calls, so
 *    the two surfaces cannot disagree about which cells are in scope.
 *  - **Empty means `null`/`undefined`/`''` — zero is a reported value.** A
 *    subsidiary with no non-controlling interests reports `0`, and that
 *    satisfies the rule. `isRole15Empty` is the shared predicate the editor
 *    uses for the same question.
 *  - **A missing driver slice skips the rule.** If neither `filingPrerequisites`
 *    nor `filingInfo` carries `NatureOfFinancialStatements`, the condition
 *    cannot evaluate to true, so nothing is flagged. Defaulting to "required"
 *    would force-fail every conversion whose prerequisite slice has not loaded.
 *
 * The message is the P.O.'s exact copy from the schema's `errorMessage`, prefixed
 * with the column label the way every other role 15 error is (the component's
 * visible-column filter in `_computeRowErrorFlags` matches on that prefix).
 */
function checkConditionalRequiredErrors(
  schema: Role15Schema,
  sheetData: Record<string, any>,
  context: SheetValidationContext
): Record<string, string> {
  const errors: Record<string, string> = {};
  const visibleColumns = resolveVisibleColumns(context);

  for (const control of schema.controls) {
    const rule = control.requiredIf;
    if (!rule) continue;

    // Resolved through the SAME rule the mounted editor and the
    // column-visibility resolver use — prereq-first, with `''` (not just
    // null/undefined) counting as absent so the read falls through to
    // filingInfo. `buildSheetValidationContext` already aliases the ACTIVE
    // filing-info slice under both state keys, so this path was never the
    // broken one; it shares the resolver so the two can never answer
    // differently for the same filing.
    const refValue =
      rule.sheet === SHEET_KEYS.filingPrerequisites
        ? resolvePrerequisiteValue(rule.field, {
            filingPrerequisites: readCrossSheetSlice(
              context,
              SHEET_KEYS.filingPrerequisites
            ),
            filingInfo: readCrossSheetSlice(context, SHEET_KEYS.filingInfo),
          })
        : readCrossSheetSlice(context, rule.sheet)?.[rule.field];
    if (refValue !== rule.equals) continue;

    for (const column of visibleColumns) {
      if (!column.visible) continue;

      const key = buildRole15ColumnKey(control.qname, column.dimension, column.period);
      if (!isRole15Empty(sheetData[key])) continue;

      errors[key] =
        `${columnLabel(column)}: ` +
        (rule.errorMessage ?? `${control.qname} is required`);
    }
  }

  return errors;
}

/**
 * Mirrors the editor's runSameSheetEqualityValidations(): compare Role 15's
 * `ComprehensiveIncome` against `ComprehensiveIncome_2` per visible column.
 * Both are calculated totals; a mismatch flags both cells Failed. This is the
 * role 15 analogue of role 12's `ProfitLoss` ↔ `ProfitLoss_2` check.
 *
 * The comparison uses {@link differsAtCentPrecision} (round to integer cents,
 * then `!==`) — the same rule the editor applies (`Math.round(v*100)/100`
 * then `!==`) — so a one-cent difference is flagged and float round-trips can't
 * mis-compare equal totals. (The cross-sheet/sum layers keep the 0.01 tolerance
 * band, which absorbs summation drift that does not apply to a same-field pair.)
 */
function checkComprehensiveIncomePairCrossValidation(
  role15Snapshot: Record<string, any>,
  context: SheetValidationContext
): Record<string, string> {
  const errors: Record<string, string> = {};

  const visibleColumns = resolveVisibleColumns(context);

  const prefix = accountingStandardToXbrlPrefix(context.accountingStandard);
  const comprehensiveIncomeQname = `${prefix}:ComprehensiveIncome`;
  const comprehensiveIncome2Qname = `${prefix}:ComprehensiveIncome_2`;

  for (const column of visibleColumns) {
    if (!column.visible) continue;

    const ciKey = buildRole15ColumnKey(comprehensiveIncomeQname, column.dimension, column.period);
    const ci2Key = buildRole15ColumnKey(comprehensiveIncome2Qname, column.dimension, column.period);

    // `ComprehensiveIncome_2` (the attribution-sum reciprocal twin) is now
    // RECOMPUTED up-front via calculateTotals_410000 (see validate()), so it is
    // present on the headless path exactly as it is in the mounted editor — both
    // sides compare two genuinely-populated, reconciled totals. This guard is
    // therefore defensive only: it still skips a column the recompute left empty
    // (e.g. a hidden column the visibility filter did not drop), so a missing
    // twin can never coerce to 0 and manufacture a phantom mismatch. (Previously
    // this was a STOP-GAP for the twin being absent from the raw backend payload;
    // the recompute removes that gap.)
    if (isRole15Empty(role15Snapshot[ci2Key])) continue;

    if (differsAtCentPrecision(role15Snapshot[ciKey], role15Snapshot[ci2Key])) {
      errors[ciKey] = `${columnLabel(column)}: Mismatch in value provided for the same field.`;
      errors[ci2Key] = `${columnLabel(column)}: Mismatch in value provided for the same field.`;
    }
  }

  return errors;
}

/**
 * Overlay Role 12's `Profit (loss)` onto Role 15's leaves — the headless mirror
 * of the editor's `pullRole12Values()`.
 *
 * Role 12 is itself an inheriting sheet (it pulls Role-13 analysis leaves), so
 * it must be RECOMPUTED WITH that inheritance before its total is read;
 * `recomputeRole12WithRole13Inheritance` owns that recipe so both validators
 * replay it identically. Reading Role 12's raw persisted total instead would
 * reintroduce exactly the stale-operand class of drift the Role-8/Role-12
 * recompute fixes closed.
 *
 * Degrades to the untouched slice when Role 12 has no data (matching
 * `applyLinkedInheritance`'s tolerated-absent behaviour), so a Phase-2 payload
 * that never carried Role 12 keeps whatever the extraction gave it.
 */
function inheritProfitLossFromRole12(
  sheetData: Record<string, any>,
  context: SheetValidationContext,
  xbrlPrefix: 'ifrs-full' | 'ifrs-smes'
): Record<string, any> {
  const role12Data = readSlice(context.crossSheetData, SHEET_KEYS.statementOfProfitOrLoss);
  if (!role12Data || Object.keys(role12Data).length === 0) return sheetData;

  const { snapshot } = recomputeRole12WithRole13Inheritance(
    role12Data as Record<string, any>,
    readSlice(context.crossSheetData, SHEET_KEYS.analysisOfProfitOrLoss) as
      | Record<string, any>
      | undefined,
    xbrlPrefix,
    context.accountingStandard
  );

  return applyLinkedInheritance(
    pickRole15Schema(context.accountingStandard).controls as unknown as LinkableControl[],
    sheetData,
    snapshot,
    ROLE12_PROFIT_OR_LOSS_LINK_ROLE
  );
}

export const role15StatementOfComprehensiveIncomeValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.statementOfComprehensiveIncome,
  // Tier 3: cross-sheet reads (filingPrerequisites for Nature/Preparation
  // gating and the conditional-required rule, + statementOfChangesInEquity for
  // the equity-axis equality check)
  // with no calculations or row-array repetition. The visibility gating is
  // a deterministic equality projection, not a true compute, so it stays
  // below Tier 4.
  tier: 3,
  validate(
    sheetData: Record<string, any>,
    context: SheetValidationContext
  ): PureSheetValidationResult {
    // Recompute Role 15's own calculated totals (ComprehensiveIncome,
    // OtherComprehensiveIncome, ComprehensiveIncome_2) up-front and overlay them
    // on the persisted slice, so the equality checks below compare the SAME
    // operands the mounted editor does (it calls calculateTotals() before
    // validating). Reading the raw N8N-extracted cells here produced a false
    // blocking mismatch on the listing that corrected itself only when the sheet
    // was opened. The raw `sheetData` is preserved for the Validated* diff below,
    // which must still see the extraction. (`recomputed` is also reused there, so
    // we calculate it exactly once.) Companion to recomputeRole19Snapshot() — the
    // recompute-up-front fix already applied to roles 8/12/19.
    const xbrlPrefix = accountingStandardToXbrlPrefix(context.accountingStandard);

    // Overlay Role 12's `Profit (loss)` FIRST — it is an inherited leaf here,
    // not this sheet's own datum (P.O. verdict, 2026-08-21). `ComprehensiveIncome
    // = ProfitLoss + OtherComprehensiveIncome`, so recomputing from the raw
    // N8N-extracted `ProfitLoss` would compute a total the mounted editor no
    // longer shows the moment Role 12 is edited — and publish it into the
    // blocking SOCIE equality below. See `inheritProfitLossFromRole12`.
    const leaves = inheritProfitLossFromRole12(sheetData, context, xbrlPrefix);

    const recomputed = calculator.calculateTotals_410000(leaves, xbrlPrefix);
    const role15Snapshot = buildEqualitySnapshot(leaves, recomputed);

    // The conditional-required rule reads the RAW leaves, never the recomputed
    // snapshot: both attribution rows are user-entered, and the question it
    // answers is "did the preparer report a value here?". Feeding it a
    // recomputed overlay would answer a different question.
    const errors: Record<string, string> = {
      ...checkConditionalRequiredErrors(
        pickRole15Schema(context.accountingStandard),
        sheetData,
        context
      ),
      ...checkCrossSheetEqualityWithRole19(role15Snapshot, context),
      ...checkComprehensiveIncomePairCrossValidation(role15Snapshot, context),
    };

    if (Object.keys(errors).length > 0) {
      return { hasErrors: true, hasSuggestedChanges: false, errors };
    }

    // AS-842 calc-diff: surface `Validated*` in the silent path by diffing
    // role 15's recomputed totals against persisted (N8N-extracted) values.
    return {
      hasErrors: false,
      hasSuggestedChanges:
        isResolvedAccountingStandard(context.accountingStandard) &&
        hasCalculatedFieldMismatch(context.extractedBaseline ?? sheetData, recomputed),
      errors,
    };
  },
};
